/* eslint-disable no-return-assign */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { Component } from 'preact';
import PropTypes from 'prop-types';
import IMask from 'imask';

import getUTM from '%/utils/utm';
import ItemCard from '../ItemCard';
import { priceMask } from '../../../utils/masks';
import FinancingOption from '../FinancingOption';
import FormMessageOverlay from '../../FormMessageOverlay';

import {
  setPersonalInformations,
  setModelInformations,
  setUsedModelValue,
  setUsedOnChange,
  setEntryValue,
  setFinancingValue,
  setFinancingSimulationData,
  setFinancingOptions,
} from '../../../stores/FinancingSimulator';
import Tooltip from '../Tooltip';
import RangeInput from '../../RangeInput';
import {
  addCurrencyFormat,
  currencyStringToNumber,
  removeCurrencyFormat,
} from '../utils';

export default class FinancingSimulation extends Component {
  constructor(props) {
    super(props);

    this.state = {
      cpf: '',
      name: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      unit: '',
      model: '',
      version: '',
      modelYear: '',
      modelThumb: '',
      modelValue: '',
      usedModelValue: '',
      usedOnChange: '',
      entryValue: '',
      financingValue: null,
      openedOptionDetails: null,
      financingOptions: [],
      financingOptionsError: null,
      financingOptionsErrorMessage: '',
      financingOptionsIsLoading: false,
      financingOptionsIsLoaded: false,
      showOverlay: false,
      entryValueIsValid: true,
      simulationFuelYear: '',
      simulationModelId: '',
      financingValueError: null,
      proposalId: null,
      installmentsId: null,
      commissionCode: null,
    };

    this.handleEntryValueChange = this.handleEntryValueChange.bind(this);
    this.simulateFinancing = this.simulateFinancing.bind(this);
    this.handleNextStep = this.handleNextStep.bind(this);
    this.handleCloseOverlay = this.handleCloseOverlay.bind(this);
    this.clearOptions = this.clearOptions.bind(this);
    this.toggleOptionDetails = this.toggleOptionDetails.bind(this);
    this.setupUpNavigateBackButton = this.setupUpNavigateBackButton.bind(this);
    this.setupUpModalOutsideClick = this.setupUpModalOutsideClick.bind(this);
    this.usedModelValueBigger = this.usedModelValueBigger.bind(this);
    this.entryRangeInputMaxValue = this.entryRangeInputMaxValue.bind(this);
    this.rangeInputMaxValueLabel = this.rangeInputMaxValueLabel.bind(this);
    this.entryInputDisabled = this.entryInputDisabled.bind(this);

    window.store.FinancingSimulator.watch(setPersonalInformations, store => {
      this.setState({
        cpf: store.cpf,
        name: store.username,
        email: store.email,
        phone: store.phone,
        dateOfBirth: store.dateOfBirth,
      });
    });

    window.store.FinancingSimulator.watch(setModelInformations, store => {
      this.setState({
        unit: store.unit,
        model: store.model,
        version: store.version,
        modelYear: store.modelYear,
        modelThumb: store.modelThumb,
        modelValue: store.modelValue,
        simulationFuelYear: store.simulationFuelYear,
        simulationModelId: store.simulationModelId,
      });
    });

    window.store.FinancingSimulator.watch(setUsedModelValue, store => {
      this.setState({
        usedModelValue: store.usedModelValue,
      });

      this.setState(prevState => ({
        financingValue: this.calculateFinancingValue(
          this.entryValueMask.unmaskedValue,
          prevState.usedModelValue,
          prevState.modelValue,
        ),
      }));
    });

    window.store.FinancingSimulator.watch(setUsedOnChange, store => {
      this.setState({
        usedOnChange: store.usedOnChange,
      });
    });

    window.store.FinancingSimulator.watch(setFinancingOptions, store => {
      this.setState({
        installmentsId: store.installmentsId,
        commissionCode: store.commissionCode,
      });
    });
  }

  componentDidMount() {
    this.entryValueMask = IMask(this.entryValueInput, priceMask);
  }

  componentDidUpdate() {
    this.setupUpNavigateBackButton();
    this.setupUpModalOutsideClick();
  }

  handleEntryValueChange(value) {
    const entryValueFormatted = value.replace('R$ ', '');

    this.setState(prevState => ({
      entryValue: entryValueFormatted,
      entryValueIsValid: true,
      financingValue: this.calculateFinancingValue(
        currencyStringToNumber(value),
        prevState.usedModelValue,
        prevState.modelValue,
      ),
    }));

    setEntryValue(entryValueFormatted);
  }

  handleOptionSelect(financingOption) {
    // eslint-disable-next-line react/no-access-state-in-setstate
    const updatedOptions = this.state.financingOptions.map(option => ({
      ...option,
      isChecked: option.subOfferId === financingOption.installmentsId,
    }));

    this.setState({
      financingOptions: updatedOptions,
    });
  }

  handleNextStep(e) {
    this.sendConversion();
    this.finishSimulation();

    this.setState({
      showOverlay: true,
    });

    e.stopImmediatePropagation();
  }

  handleCloseOverlay() {
    this.setState({
      showOverlay: false,
    });
  }

  handleValidation() {
    const formInputs = [
      {
        element: this.entryValueInput,
        stateKey: 'entryValueIsValid',
      },
    ];

    let formIsValid = true;

    formInputs.forEach(({ element, stateKey }) => {
      let { value } = element;
      value = value.trim();

      if (value === '' || value === 'R$') {
        this.setState({
          [stateKey]: false,
        });

        formIsValid = false;
      } else {
        this.setState({
          [stateKey]: true,
        });
      }
    });

    return formIsValid;
  }

  setupUpNavigateBackButton() {
    if (
      this.navigateBackButton &&
      !this.navigateBackButton.getAttribute('data-has-event-listener')
    ) {
      window.handlePrevStep(this.navigateBackButton);
      this.navigateBackButton.setAttribute('data-has-event-listener', true);
    }
  }

  setupUpModalOutsideClick() {
    const successOverlay = document.querySelector('.form-message-overlay');
    if (successOverlay && !successOverlay.onclick) {
      window.handleNextStep(successOverlay);

      successOverlay.onclick = ({ target, currentTarget }) => {
        if (target !== currentTarget) return;
        this.handleCloseOverlay();
      };
    }
  }

  getConversionParams() {
    const {
      model: product,
      version,
      unit,
      modelValue,
      username: name,
      phone,
      cpf,
      email,
      dateOfBirth,
      mailing,
      phoning,
      whatsapping,
      usedOnChange,
      usedModelBrand,
      usedModelName,
      usedModelYear,
      usedModelKM,
      usedModelFipeCode,
      usedModelValue: usedModelSaleValue,
      entryValue,
      installments,
      installmentValue,
      financingValue,
      productId,
      financedValue,
      financingMinEntryValue,
    } = window.store.FinancingSimulator.getState();

    return {
      bait: '-7',
      paymentMethod: 'Quero financiar',
      SubOrigem__c: 'DDM Financiamento',
      category: this.props.category,
      utmz: getUTM(),
      brand: this.props.brand,
      channel: this.props.channel,
      unit,
      product_id: productId,
      product,
      version,
      modelValue,
      cpf,
      dateOfBirth,
      email,
      name,
      mailing,
      phone,
      phoning,
      whatsapping,
      usedOnChange,
      usedModelBrand,
      usedModelName,
      usedModelYear,
      usedModelKM,
      usedModelFipeCode,
      usedModelSaleValue,
      entryValue,
      installments,
      installmentValue,
      financingValue,
      financedValue: this.maskedValue(financedValue),
      financingMinEntryValue: this.maskedValue(financingMinEntryValue),
    };
  }

  async getFinancingSimulationData(proposalId) {
    if (!proposalId) return [];

    try {
      const { store_id } = this.props;

      const body = {
        proposalId,
        store_code: store_id,
      };

      const data = await window.service.checkFinancingSimulation(body);

      const { offers } = data;

      const hasOffers = offers && offers.length > 0;
      const hasSubOffers = hasOffers ? offers[0].subOffers : false;

      const financingOptions = hasSubOffers ? offers[0].subOffers : [];
      const financedValue = hasOffers ? offers[0].financedValue : 0;
      const financingMinEntryValue = hasOffers ? offers[0].minEntryValue : 0;
      const financingEntryValueRecommended = hasOffers
        ? offers[0].entryValueRecommended
        : 0;

      const errorDescription = data.errorDescription || '';

      setFinancingSimulationData({
        financedValue,
        financingMinEntryValue,
        financingEntryValueRecommended,
      });

      return {
        financingOptions,
        financedValue,
        financingMinEntryValue,
        financingEntryValueRecommended,
        errorDescription,
      };
    } catch (error) {
      throw new Error('Falha ao capturar as financing options');
    }
  }

  toggleOptionDetails(index) {
    // eslint-disable-next-line react/no-access-state-in-setstate
    const newValue = this.state.openedOptionDetails === index ? null : index;

    this.setState({
      openedOptionDetails: newValue,
    });
  }

  async findProposalId(simulationData) {
    if (!simulationData) return null;

    try {
      const data = await window.service.createFinancingSimulation(
        simulationData,
      );

      const proposalId = data.proposalId || null;

      this.setState({ proposalId });

      return proposalId;
    } catch (error) {
      throw new Error('Falha ao capturar o proposal ID');
    }
  }

  async checkSimulation(proposalId) {
    let attempts = 0;
    let intervalID;

    const maxAttempts = 60;
    const delay = 1000;

    return new Promise((resolve, reject) => {
      intervalID = setInterval(async () => {
        try {
          const {
            financingOptions,
            financingMinEntryValue,
            financingEntryValueRecommended,
            errorDescription,
          } = await this.getFinancingSimulationData(proposalId);

          if (errorDescription.length > 0) {
            clearInterval(intervalID);

            this.setState({
              financingOptionsError: true,
              financingOptionsErrorMessage: errorDescription,
              financingOptionsIsLoading: false,
              financingOptionsIsLoaded: false,
            });
          }

          if (financingOptions.length > 0) {
            clearInterval(intervalID);
            resolve({
              financingOptions,
              financingMinEntryValue,
              financingEntryValueRecommended,
            });
          }

          // eslint-disable-next-line no-plusplus
          attempts++;

          if (attempts >= maxAttempts) {
            clearInterval(intervalID);
            reject(
              new Error(
                'Limite máximo de tentativas atingido. Resposta desejada não foi recebida.',
              ),
            );
          }
        } catch (error) {
          clearInterval(intervalID);
          reject(error);
        }
      }, delay);
    });
  }

  async finishSimulation() {
    const { store_id } = this.props;
    const { proposalId, installmentsId, commissionCode } = this.state;

    try {
      const body = {
        proposalId,
        store_code: store_id,
        subOfferId: installmentsId,
        commissionCode,
      };

      await window.service.finishFinancingSimulation(body);
    } catch (error) {
      throw new Error('Falha ao finalizar a simulação de financiamento');
    }
  }

  sendConversion() {
    const params = this.getConversionParams();

    return window.serviceConversion
      .convert(params)
      .then(() => {})
      .catch(error => {
        console.error(error);
      });
  }

  maskedValue(number) {
    return number.toLocaleString('pt-br', { minimumFractionDigits: 2 });
  }

  unmaskedValue(number) {
    const formatedNumber = Number(
      number.replace(/[^0-9,-]+/g, '').replace(',', '.'),
    );

    return formatedNumber;
  }

  calculateFinancingValue(entryValue, usedModelValue, modelValue) {
    const entryValueFormated = Number(entryValue);
    const usedModelValueFormated = this.unmaskedValue(usedModelValue);
    const modelValueFormated = this.unmaskedValue(modelValue);

    let financingValue =
      modelValueFormated - (entryValueFormated + usedModelValueFormated);
    financingValue = financingValue > 0 ? financingValue : 0;

    setFinancingValue(this.maskedValue(financingValue));

    return financingValue;
  }

  validateFinancingValue() {
    const { financingValue } = this.state;

    const financingValueIsValid = financingValue !== 0;

    this.setState({
      financingValueError: !financingValueIsValid,
      financingOptionsError: null,
    });

    return financingValueIsValid;
  }

  async simulateFinancing(e) {
    e.preventDefault();

    const { store_id } = this.props;
    const formIsValid = this.handleValidation();
    const financingValueIsValid = this.validateFinancingValue();

    if (formIsValid && financingValueIsValid) {
      const simulationData = {
        cpf: this.state.cpf,
        name: this.state.name,
        email: this.state.email,
        phone: this.state.phone,
        birth_date: this.state.dateOfBirth,
        st_vehicle_model: this.state.simulationModelId,
        st_vehicle_fuelYear: this.state.simulationFuelYear,
        st_manufacture_year: this.state.modelYear,
        st_vehicle_value: this.unmaskedValue(this.state.modelValue),
        store_code: store_id,
      };

      try {
        this.setState({
          financingOptionsError: null,
          financingOptionsIsLoading: true,
          financingOptionsIsLoaded: false,
        });

        const proposalId = await this.findProposalId(simulationData);
        const { financingOptions } = await this.checkSimulation(proposalId);

        this.setState({
          financingOptions,
          financingOptionsIsLoading: false,
          financingOptionsIsLoaded: true,
        });
      } catch (error) {
        this.setState({
          financingOptionsError: true,
          financingOptionsIsLoading: false,
          financingOptionsIsLoaded: false,
        });

        console.error(error);
      }
    } else {
      this.setState({
        financingOptionsIsLoading: false,
        financingOptionsIsLoaded: false,
      });
    }
  }

  clearOptions() {
    this.setState({
      financingOptions: [],
      financingOptionsIsLoaded: false,
    });
  }

  /* verifica se o valor do veículo usado é menor que
  o valor do veículo simulado no financiamento. */
  usedModelValueBigger() {
    const { modelValue, usedModelValue } = this.state;

    return (
      currencyStringToNumber(usedModelValue) >
      currencyStringToNumber(modelValue)
    );
  }

  entryInputDisabled() {
    return this.usedModelValueBigger();
  }

  entryInputValue() {
    const { entryValue } = this.state;

    if (this.usedModelValueBigger()) return 'R$ 0';

    return entryValue ? `R$ ${entryValue}` : entryValue;
  }

  entryRangeInputValue() {
    if (this.usedModelValueBigger()) return 0;

    const { entryValue } = this.state;

    return Number(removeCurrencyFormat(entryValue));
  }

  /* retorna o valor máximo que o usuário pode
  inserir no campo de entrada do financiamento */
  entryRangeInputMaxValue() {
    /* O valor "-1" impede o usuário de inserir o valor total do modelo
    como entrada, o que tornaria o financiamento sem sentido. */
    if (this.usedModelValueBigger()) return 0;

    const { modelValue, usedModelValue } = this.state;
    const numberModelValue = currencyStringToNumber(modelValue);
    const numberUsedModelValue = currencyStringToNumber(usedModelValue);

    return !usedModelValue
      ? numberModelValue - 1
      : numberModelValue - numberUsedModelValue - 1;
  }

  rangeInputMaxValueLabel() {
    const value = this.entryRangeInputMaxValue();

    return `R$ ${value.toLocaleString('pt-br', {
      minimumFractionDigits: 2,
    })}`;
  }

  render() {
    const {
      model,
      version,
      unit,
      modelThumb,
      modelValue,
      usedOnChange,
      usedModelValue,
      financingValue,
      financingOptions,
      financingOptionsError,
      financingOptionsErrorMessage,
      financingOptionsIsLoading,
      financingOptionsIsLoaded,
      showOverlay,
      entryValueIsValid,
      financingValueError,
      openedOptionDetails,
    } = this.state;

    return (
      <div className="financing-simulation">
        <h2 className="step__title">
          {financingOptionsIsLoaded
            ? 'Selecione a melhor opção de financiamento para você'
            : 'Simulações de financiamento'}
        </h2>
        <h6 className="step__subtitle">
          A contratação do financiamento está sujeita à análise e aprovação de
          crédito pelo Santander.
        </h6>

        <div className="row">
          {!financingOptionsIsLoaded && (
            <div className="col-lg-8 col-md-12">
              <div className="form-group">
                <label
                  className="bold-label entry-value-label"
                  htmlFor="entry-value"
                >
                  Informe o valor de entrada:
                  <Tooltip
                    message="Quantia a ser paga à vista pelo comprador ao fechar o contrato de financiamento."
                    mobileDirection="left"
                  >
                    <i className="icon icon-info" />
                  </Tooltip>
                </label>

                <RangeInput
                  value={this.entryRangeInputValue()}
                  max={this.entryRangeInputMaxValue()}
                  onInput={({ target }) =>
                    this.handleEntryValueChange(target.value)
                  }
                  onChange={({ target }) =>
                    this.handleEntryValueChange(addCurrencyFormat(target.value))
                  }
                />
                <div className="range-input__bottom-price-range">
                  <div>R$ 0,00</div>
                  <div>{this.rangeInputMaxValueLabel()}</div>
                </div>

                <input
                  onChange={({ target }) =>
                    this.handleEntryValueChange(target.value)
                  }
                  value={this.entryInputValue()}
                  type="text"
                  className="form-control"
                  name="entry-value"
                  required
                  placeholder="Ex: R$ 35.000"
                  data-bouncer-target="#invalid-entry-value"
                  ref={entryValueInput =>
                    (this.entryValueInput = entryValueInput)
                  }
                  disabled={this.entryInputDisabled()}
                />

                {!entryValueIsValid && (
                  <div
                    id="invalid-entry-value"
                    className="invalid-feedback is-invalid-entry-value"
                  >
                    <div
                      className="error-message"
                      id="bouncer-error_entry-value"
                    >
                      Por favor, informe esse campo
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="bold-label" htmlFor="used-model-value">
                  Valor do seminovo para negociação:
                </label>
                <input
                  onChange={() => {}}
                  value={`R$ ${usedModelValue || '0,00'}`}
                  type="text"
                  className="form-control"
                  name="used-model-value"
                  placeholder="Ex: R$ 50.000"
                  data-bouncer-target="#invalid-used-model-value"
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="bold-label" htmlFor="financing-total-value">
                  Valor total do financiamento:
                </label>
                <div className="financing-total-value-box">
                  <h5>
                    R${' '}
                    {financingValue
                      ? this.maskedValue(financingValue)
                      : modelValue}
                  </h5>
                </div>
              </div>
            </div>
          )}

          <div
            className={`financing-simulation__financing-options-content col-lg-8 col-md-12 ${
              financingOptionsIsLoaded && 'd-block'
            }`}
          >
            <div className="financing-simulation__financing-options">
              {financingOptions &&
                financingOptions.map((options, index) => (
                  <FinancingOption
                    key={options.subOfferId}
                    id={options.subOfferId}
                    commissionCode={options.defaultCommissionCode}
                    installments={options.term}
                    installmentValue={options.installmentsValue}
                    checkedInInitialization={index === 0}
                    onSelect={financingOption =>
                      this.handleOptionSelect(financingOption)
                    }
                    isChecked={options.isChecked}
                    onOpenDetails={() => this.toggleOptionDetails(index)}
                    isOpen={openedOptionDetails === index}
                  />
                ))}
            </div>

            <div className="financing-simulation__security-text">
              <div>
                <i className="icon icon-security" />
              </div>
              <span>Fique tranquilo! Essa simulação é sem compromisso.</span>
            </div>

            <hr />
            <p className="financing-simulation__authorization-text">
              Ao continuar, você autoriza que o Santander acesse suas
              informações e está ciente de que poderá ser contatado por e-mail,
              telefone, WhatsApp ou SMS, para recebimento de ofertas de produtos
              e serviços do Conglomerado Santander. A contratação do
              financiamento está sujeita à análise e aprovação de crédito pelo
              Santander. Consulte sempre as condições, o Custo Efetivo total
              (CET) e a nossa{' '}
              <a
                href="https://500e.fiat.com.br/content/dam/fiat/itaucard/tabela-financiamento-itau/financiamento-itau.pdf"
                className="text-primary"
                target="blank"
              >
                &quot;Tabela de tarifas&quot;
              </a>{' '}
              previamente a contratação. Saiba mais sobre como o Santander trata
              dados pessoais na &quotPolítica de Privacidade&quot disponível em
              seus sites e aplicativos.
            </p>
            <hr />

            <div className="btns-group">
              <button
                type="button"
                className="btn btn-prev--outline font-weight-bold"
                onClick={this.clearOptions}
              >
                Anterior
              </button>
              <button
                type="button"
                className="btn btn-next button--primary font-weight-bold"
                onClick={this.handleNextStep}
              >
                Enviar proposta
              </button>
            </div>
          </div>

          <div
            className={`col-lg-4 col-md-12 ${
              financingOptionsIsLoaded && 'd-none d-lg-block'
            }`}
          >
            <div className="financing-simulation__card">
              <ItemCard
                modelName={model}
                version={version}
                unit={unit}
                thumb={modelThumb}
                thumbAlt={model}
                textPrice="Valor do modelo"
                price={modelValue}
              />
            </div>
          </div>

          <div
            className={`
            col-lg-8 col-md-12 ${financingOptionsIsLoaded && 'd-none'}
          `}
          >
            <div className="btns-group">
              <button
                type="button"
                className="btn btn-prev--outline font-weight-bold"
                data-jump-to-personal-information={`${usedOnChange}`}
                ref={navigateBackButton =>
                  (this.navigateBackButton = navigateBackButton)
                }
              >
                Anterior
              </button>
              <button
                type="button"
                className="btn button--primary font-weight-bold"
                onClick={this.simulateFinancing}
              >
                Ver propostas
              </button>
            </div>
          </div>

          {financingOptionsIsLoading && (
            <div className="col-lg-8 col-md-12">
              <div className="financing-simulation__load-box">
                <div className="financing-simulation__load-box-content">
                  <div>
                    <div className="spinner" role="status" />
                  </div>
                  <h5>Buscando as melhores propostas de financiamento...</h5>
                </div>
              </div>
            </div>
          )}

          {financingValueError && (
            <div className="col-lg-8 col-md-12">
              <div className="financing-simulation__user-error-box">
                <i className="icon icon-alert-circle" />
                <h5>
                  Os valores de entrada informados superam o valor total do
                  veículo. Por favor, informe valores válidos.
                </h5>
              </div>
            </div>
          )}

          {financingOptionsError && (
            <div className="col-lg-8 col-md-12">
              <div className="financing-simulation__error-box">
                <div className="financing-simulation__error-box-content">
                  <h5>
                    {financingOptionsErrorMessage ||
                      'Tivemos um problema no momento e não foi possivel seguir com a análise de crédito. Por favor, tente novamente mais tarde.'}
                  </h5>
                </div>
              </div>
            </div>
          )}
        </div>

        <FormMessageOverlay
          type="success"
          successMessage="Proposta enviada com sucesso!"
          handleClose={this.handleCloseOverlay}
          isVisible={showOverlay}
          content={{
            success: {
              iconClass: 'icon icon-check',
            },
          }}
        />
      </div>
    );
  }
}

FinancingSimulation.defaultProps = {
  category: '',
  brand: '',
  channel: '',
  store_id: '',
};

FinancingSimulation.propTypes = {
  category: PropTypes.string,
  brand: PropTypes.string,
  channel: PropTypes.string,
  store_id: PropTypes.string,
};
